import { useState } from "react";
import { franchisePage } from "@/mocks/franchise";
import GlobalBottomCta from "@/components/feature/GlobalBottomCta";
import PageMeta from "@/components/feature/PageMeta";
import ScrollReveal from "@/components/feature/ScrollReveal";

const pageMeta = {
  title: "紅豆餅加盟｜來加綉綉紅豆餅加盟專區",
  description: "想經營紅豆餅副業、擺攤、市集、店面或甜點品牌？來加綉綉紅豆餅加盟專區提供品牌系統、產品口味、開店支援與營運陪跑諮詢。",
  keywords: "紅豆餅加盟,車輪餅加盟,甜點加盟,小資創業,副業創業,擺攤加盟,加盟諮詢",
  ogType: "article",
};

export default function FranchisePage() {
  const { hero, targetAudience, value, process, faq } = franchisePage;
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const toggleFaq = (idx: number) => {
    setOpenFaq(openFaq === idx ? null : idx);
  };

  return (
    <main className="pt-16 md:pt-20">
      <PageMeta {...pageMeta} />
      {/* Hero */}
      <section className="relative min-h-[380px] md:min-h-[460px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-redbean-primary/40">
          <img
            src="https://readdy.ai/api/search-image?query=red%20bean%20cake%20street%20vendor%20cart%20Japanese%20taiyaki%20food%20stall%20night%20market%20warm%20golden%20lighting%20entrepreneur%20small%20business%20scene%20cozy%20street%20food%20atmosphere%20artisan%20handmade%20dessert%20vendor%20Taiwan%20night%20market%20scene%20warm%20amber%20tones&width=1600&height=900&seq=fr-hero-1&orientation=landscape"
            alt="紅豆餅加盟創業市集擺攤場景"
            className="w-full h-full object-cover object-center"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-redbean-coffee/55 via-redbean-coffee/45 to-redbean-coffee/60" />
        </div>
        <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-8 text-center" style={{ maxWidth: "900px" }}>
          <span className="inline-block px-4 py-1.5 bg-redbean-gold text-redbean-coffee text-sm font-semibold rounded-full mb-6">
            {hero.tag}
          </span>
          <h1
            className="font-heading font-black text-redbean-cream mb-5 max-w-3xl mx-auto text-balance"
            style={{ fontSize: "clamp(2rem, 5vw, 2.75rem)", lineHeight: 1.3 }}
          >
            {hero.title}
          </h1>
          <p className="text-redbean-cream/85 max-w-2xl mx-auto" style={{ fontSize: "1.1875rem", lineHeight: 1.75 }}>
            {hero.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <a
              href="https://lin.ee/xiNNjjf"
              rel="nofollow"
              target="_blank"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-redbean-gold text-redbean-coffee font-semibold rounded-full hover:bg-redbean-cream transition-colors whitespace-nowrap text-[1.0625rem]"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-chat-3-line" />
              </span>
              {hero.ctaPrimary}
            </a>
            <a
              href="#franchise-support"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border-2 border-redbean-cream text-redbean-cream font-semibold rounded-full hover:bg-redbean-cream hover:text-redbean-coffee transition-colors whitespace-nowrap text-[1.0625rem]"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("franchise-support")?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-lightbulb-line" />
              </span>
              {hero.ctaSecondary}
            </a>
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-16 md:py-20 bg-redbean-cream">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: "1200px" }}>
          <ScrollReveal>
            <h2
              className="font-heading font-bold text-redbean-coffee text-center mb-10 md:mb-12"
              style={{ fontSize: "clamp(1.5rem, 3vw, 2.125rem)", lineHeight: 1.4 }}
            >
              {targetAudience.sectionTitle}
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {targetAudience.cards.map((card, idx) => (
              <ScrollReveal key={idx} delay={idx * 120}>
                <div className="bg-white rounded-card p-6 md:p-7 shadow-soft">
                  <span className="inline-block px-3 py-1 bg-redbean-primary/10 text-redbean-primary text-sm font-semibold rounded-full mb-4">
                    {card.label}
                  </span>
                  <h3
                    className="font-heading font-bold text-redbean-coffee mb-2"
                    style={{ fontSize: "1.25rem", lineHeight: 1.5 }}
                  >
                    {card.title}
                  </h3>
                  <p className="text-redbean-coffee/60 text-[1rem] leading-relaxed">{card.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Value */}
      <section id="franchise-support" className="py-16 md:py-20 bg-redbean-lightcream">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: "1200px" }}>
          <ScrollReveal>
            <h2
              className="font-heading font-bold text-redbean-coffee text-center mb-6"
              style={{ fontSize: "clamp(1.5rem, 3vw, 2.125rem)", lineHeight: 1.4 }}
            >
              {value.sectionTitle}
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={100}>
            <p
              className="text-redbean-coffee/70 text-center max-w-3xl mx-auto mb-10 md:mb-12"
              style={{ fontSize: "1.1875rem", lineHeight: 1.75 }}
            >
              {value.intro}
            </p>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {value.supports.map((item, idx) => {
              const icons = ["ri-shield-star-line", "ri-store-2-line", "ri-heart-pulse-line"];
              return (
                <ScrollReveal key={idx} delay={idx * 120}>
                  <div className="bg-white rounded-card p-6 md:p-7 shadow-soft text-center">
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-redbean-primary/10 text-redbean-primary">
                      <span className="w-7 h-7 flex items-center justify-center">
                        <i className={`${icons[idx]} text-2xl`} />
                      </span>
                    </div>
                    <h3
                      className="font-heading font-bold text-redbean-coffee mb-2"
                      style={{ fontSize: "1.25rem", lineHeight: 1.5 }}
                    >
                      {item.title}
                    </h3>
                    <p className="text-redbean-coffee/60 text-[1rem] leading-relaxed">{item.desc}</p>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 md:py-20 bg-redbean-cream">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: "1200px" }}>
          <ScrollReveal>
            <h2
              className="font-heading font-bold text-redbean-coffee text-center mb-10 md:mb-14"
              style={{ fontSize: "clamp(1.5rem, 3vw, 2.125rem)", lineHeight: 1.4 }}
            >
              {process.sectionTitle}
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {process.steps.map((item, idx) => (
              <ScrollReveal key={idx} delay={idx * 100}>
                <div className="bg-white rounded-card p-6 md:p-7 shadow-soft relative">
                  <span className="inline-block px-3 py-1 bg-redbean-gold/20 text-redbean-caramel text-sm font-bold rounded-full mb-4">
                    {item.step}
                  </span>
                  <h3
                    className="font-heading font-bold text-redbean-coffee mb-2"
                    style={{ fontSize: "1.25rem", lineHeight: 1.5 }}
                  >
                    {item.title}
                  </h3>
                  <p className="text-redbean-coffee/60 text-[1rem] leading-relaxed">{item.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-redbean-lightcream">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: "800px" }}>
          <ScrollReveal>
            <h2
              className="font-heading font-bold text-redbean-coffee text-center mb-10 md:mb-12"
              style={{ fontSize: "clamp(1.5rem, 3vw, 2.125rem)", lineHeight: 1.4 }}
            >
              {faq.sectionTitle}
            </h2>
          </ScrollReveal>
          <div className="space-y-3">
            {faq.questions.map((item, idx) => (
              <ScrollReveal key={idx} delay={idx * 80}>
                <div className="bg-white rounded-card shadow-soft overflow-hidden">
                  <button
                    type="button"
                    onClick={() => toggleFaq(idx)}
                    className="w-full flex items-center justify-between p-5 md:p-6 text-left"
                  >
                    <span
                      className="font-heading font-bold text-redbean-coffee pr-4"
                      style={{ fontSize: "1.0625rem", lineHeight: 1.5 }}
                    >
                      {item.q}
                    </span>
                    <span className="w-8 h-8 shrink-0 flex items-center justify-center rounded-full bg-redbean-primary/10 text-redbean-primary transition-transform duration-300">
                      <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${openFaq === idx ? "rotate-180" : ""}`} />
                    </span>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${openFaq === idx ? "max-h-60" : "max-h-0"}`}
                  >
                    <p className="px-5 md:px-6 pb-5 md:pb-6 text-redbean-coffee/60 text-[1rem] leading-relaxed">
                      {item.a}
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <GlobalBottomCta />
    </main>
  );
}